import React, { useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import AdminPage from '../../../admin/shared/AdminPage'
import OperationalTable from '../../../../organisms/OperationalTable'
import { useSearchQueryBlueprints } from '../../../../../api/denali'
import Loading from '../../../../molecules/Loading'
import ErrorDisplay from '../../../../molecules/ErrorDisplay'
import EmptySection from '../../../../atoms/EmptySection'
import SydActionMenu from '../../../../commonDesign/SydActionMenu'
import SydButton from '../../../../commonDesign/Button'
import FlexRow from '../../../../molecules/FlexRow'
import CreateQueryBlueprintDialog from './CreateQueryBlueprintDialog'
import DeleteQueryBlueprintDialog from './DeleteQueryBlueprintDialog'

function QueryBlueprintsHome ({ root }) {
  const history = useHistory()
  const createRef = useRef(null)
  const deleteRef = useRef(null)

  const { data, isLoading, error, refetch } = useSearchQueryBlueprints({
    skip: 0,
    take: 200
  })

  const handleRowClick = useCallback((row) => {
    history.push(`${root}/analysis/query-blueprints/${row.original.blueprintId}`)
  }, [history, root])

  const handleCreateComplete = useCallback((result) => {
    if (result?.blueprintId) {
      history.push(`${root}/analysis/query-blueprints/${result.blueprintId}`)
    } else {
      refetch()
    }
  }, [history, root, refetch])

  const columns = [
    { id: 'blueprintId', accessor: 'blueprintId', Header: 'ID' },
    { id: 'code', accessor: 'code', Header: 'Code' },
    { id: 'name', accessor: 'name', Header: 'Name' },
    { id: 'description', accessor: 'description', Header: 'Description' },
    {
      id: 'enabled',
      accessor: 'enabled',
      Header: 'Enabled',
      Cell: ({ value }) => (value ? 'Yes' : 'No')
    },
    { id: 'definitionVersionId', accessor: 'definitionVersionId', Header: 'Active Version' },
    {
      id: 'actions',
      Header: 'Actions',
      disableSortBy: true,
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => (
        <SydActionMenu
          actions={[
            // eslint-disable-next-line react/prop-types
            { key: 'delete', label: 'Delete', icon: 'delete', onClick: () => deleteRef.current.open(row.original) }
          ]}
        />
      )
    }
  ]

  if (error) {
    return (
      <AdminPage>
        <ErrorDisplay error={error} onRetry={refetch} />
      </AdminPage>
    )
  }

  if (isLoading) {
    return (
      <AdminPage>
        <Loading />
      </AdminPage>
    )
  }

  const hasData = data?.data && data.data.length > 0

  if (!hasData && !isLoading) {
    return (
      <AdminPage>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <EmptySection
              title='No Query Blueprints Yet'
              description={(
                <FlexRow>
                  <SydButton
                    variant='primary'
                    size='lg'
                    icon='add'
                    onClick={() => createRef.current.open()}
                  >
                    Create Your First Query Blueprint
                  </SydButton>
                </FlexRow>
              )}
            />
          </Grid>
        </Grid>

        <CreateQueryBlueprintDialog ref={createRef} onComplete={handleCreateComplete} />
        <DeleteQueryBlueprintDialog ref={deleteRef} onComplete={refetch} />
      </AdminPage>
    )
  }

  return (
    <AdminPage>
      <Grid container spacing={2}>
        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h1>Query Blueprints</h1>
          <div style={{ display: 'flex', gap: '8px' }}>
            <SydButton onClick={() => history.push(`${root}/analysis/query-blueprints/parse-dql`)} icon='search'>
              Parse DQL
            </SydButton>
            <SydButton onClick={() => createRef.current.open()} icon='add'>
              Create Query Blueprint
            </SydButton>
          </div>
        </Grid>
        <Grid item xs={12}>
          <OperationalTable
            variant='v2'
            columns={columns}
            data={data?.data || []}
            isLoading={isLoading}
            defaultSort={[{ id: 'name', desc: false }]}
            onRowClick={handleRowClick}
          />
        </Grid>
      </Grid>

      <CreateQueryBlueprintDialog ref={createRef} onComplete={handleCreateComplete} />
      <DeleteQueryBlueprintDialog ref={deleteRef} onComplete={refetch} />
    </AdminPage>
  )
}

QueryBlueprintsHome.propTypes = {
  root: PropTypes.any
}

export default QueryBlueprintsHome
